// Service for the admin "Access Control" page: roles, the permission catalog,
// and per-account permissions. All writes (and the operator listing) go through
// operations.admin_* SECURITY DEFINER RPCs that re-verify the caller is an active
// admin server-side using (operator id + operator_code) — RealityOperator has no
// Supabase Auth session, so RLS can't identify the admin and direct table writes
// to operations.operators are blocked. See migration
// 20260609130000_operator_access_admin.sql.
import { supabase } from '../lib/supabase';
import { Operator, Role, PermissionDef, AdminOperator } from '../types/database';

// One (role, permission) assignment row from operations.role_permissions.
export interface RolePermissionRow {
  role: string;
  permission: string;
}

// Pull (id, code) admin proof off the acting operator. The RPCs reject if these
// don't identify an active admin.
function actorArgs(actor: Operator): { p_actor_id: string; p_actor_code: string } {
  return { p_actor_id: actor.id, p_actor_code: actor.operator_code ?? '' };
}

// Supabase surfaces a RAISE EXCEPTION as { error: { message } }; the message is
// our coded string e.g. "not_authorized: active admin required". Rethrow it so
// the UI can show something meaningful.
function unwrap<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

async function callRpc<T = any>(fn: string, args: Record<string, unknown>): Promise<T> {
  return unwrap<T>(await supabase.schema('operations').rpc(fn, args));
}

export const adminAccessService = {
  // ---- Reads (catalogs are anon-readable; operators require the admin RPC) ----

  async listRoles(): Promise<Role[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('roles')
      .select('*')
      .order('sort_order');
    if (error) throw new Error(error.message);
    return (data ?? []) as Role[];
  },

  async listPermissions(): Promise<PermissionDef[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('permissions')
      .select('*')
      .order('category')
      .order('sort_order');
    if (error) throw new Error(error.message);
    return (data ?? []) as PermissionDef[];
  },

  async listRolePermissions(): Promise<RolePermissionRow[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('role_permissions')
      .select('role, permission');
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({ role: r.role, permission: (r.permission as string).trim() }));
  },

  async listOperators(actor: Operator): Promise<AdminOperator[]> {
    const data = await callRpc<AdminOperator[]>('admin_list_operators', actorArgs(actor));
    return (data ?? []).map((o) => ({
      ...o,
      operator_code: o.operator_code ? o.operator_code.trim() : o.operator_code,
      permissions: o.permissions ?? {},
    }));
  },

  // ---- Role permission grants (affect everyone with the role) ----

  async setRolePermission(actor: Operator, role: string, permission: string, enabled: boolean) {
    return callRpc('admin_set_role_permission', {
      ...actorArgs(actor),
      p_role: role,
      p_permission: permission,
      p_enabled: enabled,
    });
  },

  // ---- Role lifecycle ----

  async createRole(actor: Operator, key: string, name: string, description?: string) {
    return callRpc('admin_create_role', {
      ...actorArgs(actor),
      p_key: key,
      p_name: name,
      p_description: description ?? null,
    });
  },

  async updateRole(actor: Operator, key: string, name: string, description?: string) {
    return callRpc('admin_update_role', {
      ...actorArgs(actor),
      p_key: key,
      p_name: name,
      p_description: description ?? null,
    });
  },

  async renameRole(actor: Operator, oldKey: string, newKey: string) {
    return callRpc('admin_rename_role', {
      ...actorArgs(actor),
      p_old_key: oldKey,
      p_new_key: newKey,
    });
  },

  async deleteRole(actor: Operator, key: string, reassignTo?: string) {
    return callRpc('admin_delete_role', {
      ...actorArgs(actor),
      p_key: key,
      p_reassign_to: reassignTo ?? null,
    });
  },

  // ---- Per-account ----

  async setOperatorName(actor: Operator, operatorId: string, name: string) {
    return callRpc('admin_set_operator_name', {
      ...actorArgs(actor),
      p_operator_id: operatorId,
      p_name: name,
    });
  },

  async setOperatorRole(actor: Operator, operatorId: string, role: string) {
    return callRpc('admin_set_operator_role', {
      ...actorArgs(actor),
      p_operator_id: operatorId,
      p_role: role,
    });
  },

  // Additive individual grant: enabled=true adds the key, enabled=false removes it.
  async setOperatorPermission(actor: Operator, operatorId: string, permission: string, enabled: boolean) {
    return callRpc('admin_set_operator_permission', {
      ...actorArgs(actor),
      p_operator_id: operatorId,
      p_permission: permission,
      p_enabled: enabled,
    });
  },
};
